import type { ReactNode } from 'react';
import { cx } from '../../lib/cx';

/**
 * Figma "Business header": the strip under a page's breadcrumb that every business, project and person page shares.
 * A 64px tile, the name in the page title style, a row of chips and one muted line, and the page's actions at the far
 * end. 18px between the two halves, 16px between the tile and the text, 6px between the name and the chips.
 */
export function IdentityHeader({
  tile,
  title,
  meta,
  actions,
}: {
  tile: ReactNode;
  title: string;
  /** Chips and the muted line, left to right. */
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-(--mbm-space-4-5)">
      <div className="flex min-w-0 items-center gap-(--mbm-space-4)">
        {tile}
        <div className="flex min-w-0 flex-col gap-(--mbm-space-1-5)">
          <h1 className="truncate text-heading-page text-text-strong">{title}</h1>
          {meta ? (
            <div className="flex flex-wrap items-center gap-(--mbm-space-2) text-body-small text-text-secondary">
              {meta}
            </div>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-(--mbm-space-2)">{actions}</div> : null}
    </header>
  );
}

/** The 64px tile: dark with initials for a business, a grey disc for a person, or an icon (a project). */
export function IdentityTile({
  children,
  shape = 'tile',
  className,
}: {
  children: ReactNode;
  shape?: 'tile' | 'disc';
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cx(
        'flex size-(--mbm-size-avatar-xl) shrink-0 items-center justify-center text-heading-h2',
        shape === 'tile'
          ? 'rounded-(--mbm-component-radius-identity-tile) bg-action-primary text-text-on-dark'
          : 'rounded-full bg-surface-field text-text-primary',
        className,
      )}
    >
      {children}
    </span>
  );
}
